/**
 * Wardrobe. Household-wide — everyone gets their own; adults see everyone's. The Worker
 * enforces that with its own WHERE clause; this page does not gate anything.
 *
 * Three tabs now, and only three. The style guide and the gap list are specced in
 * PRD-wardrobe.md and not built, so they get no tab — an empty tab promising a screen
 * that does not exist is worse than no tab, the same call made in the insurance module.
 *
 * Order is deliberate: the inventory is what exists, outfits are what you make of it,
 * the plan is what you do with them.
 */
import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { WardrobeOccasion } from '../../shared/types';
import GapsPanel from './GapsPanel';
import IntakePanel from './IntakePanel';
import ItemsPanel from './ItemsPanel';
import StyleGuidePanel from './StyleGuidePanel';
import OutfitsPanel from './OutfitsPanel';
import PlanPanel from './PlanPanel';
import { ErrorState, Loading, cx, useResource } from './ui';

const TABS = [
  { id: 'items', label: 'Wardrobe' },
  { id: 'outfits', label: 'Outfits' },
  { id: 'plan', label: 'Plan' },
  { id: 'gaps', label: 'To buy' },
  { id: 'guide', label: 'Style guide' },
  { id: 'intake', label: 'Intake' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function WardrobePage() {
  const [active, setActive] = useState<TabId>('items');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Occasions drive the suggest controls and come from D1 config, never a hardcoded list.
  const occasions = useResource<WardrobeOccasion[]>(
    () => api.get<WardrobeOccasion[]>('/wardrobe/occasions'),
    []
  );

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    else return;

    e.preventDefault();
    setActive(TABS[next].id);
    tabRefs.current[next]?.focus();
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-ink">Wardrobe</h1>
        <p className="mt-1 max-w-prose text-ink-soft">
          Everything you own, outfits built from it, a week you can plan &mdash; and an
          honest list of what&rsquo;s missing.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Wardrobe sections"
        className="mb-6 flex gap-2 overflow-x-auto border-b border-border pb-px"
      >
        {TABS.map((tab, index) => (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            role="tab"
            id={`wtab-${tab.id}`}
            aria-selected={active === tab.id}
            aria-controls={`wpanel-${tab.id}`}
            tabIndex={active === tab.id ? 0 : -1}
            onClick={() => setActive(tab.id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cx(
              'min-h-11 shrink-0 rounded-t-lg border-b-2 px-4 text-sm font-medium whitespace-nowrap transition-colors',
              'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none',
              active === tab.id
                ? 'border-sage text-sage'
                : 'border-transparent text-ink-soft hover:bg-surface-sunk hover:text-ink'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`wpanel-${active}`}
        aria-labelledby={`wtab-${active}`}
        tabIndex={0}
        className="focus-visible:outline-none"
      >
        {active === 'items' && <ItemsPanel />}
        {active === 'plan' && <PlanPanel />}
        {active === 'gaps' && <GapsPanel />}
        {active === 'guide' && <StyleGuidePanel />}
        {active === 'intake' && <IntakePanel />}
        {active === 'outfits' && (
          <>
            {occasions.loading && <Loading label="Loading occasions" rows={2} />}
            {occasions.error && !occasions.loading && (
              <ErrorState message={occasions.error} onRetry={occasions.reload} />
            )}
            {occasions.data && !occasions.loading && !occasions.error && (
              <OutfitsPanel occasions={occasions.data} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
